import { PackagePreviewRow } from 'app/_models/result';

export type LedgerSectionId = 'contribution' | 'aid';
export type PackageSide = 'published' | 'draft';
export type MethodologyNeedKey = 'fmNeed' | 'imNeed';
export type OutcomeKey = 'adjNeed' | 'remainingCoa';

export interface PackageRule {
  population: string;
  category: string;
  assignType: string;
  factor: number | null;
  input: string;
  amount: number | null;
}

export interface TotalsSnapshot {
  fmNeed: number | null;
  imNeed: number | null;
  adjNeed: number | null;
  remainingCoa: number | null;
}

export interface PackageStep {
  amount: number | null;
  fmNeed: number | null;
  imNeed: number | null;
  adjNeed: number | null;
  remainingCoa: number | null;
  rules: PackageRule[];
  before: TotalsSnapshot | null;
}

export interface PackageCompare {
  packId: string;
  displayName: string;
  isContribution: boolean;
  published: PackageStep | null;
  draft: PackageStep | null;
  hasDifference: boolean;
  expanded: boolean;
}

export interface LedgerSection {
  id: LedgerSectionId;
  title: string;
  note?: string;
  amountLabel: string;
  packages: PackageCompare[];
}

export interface LedgerColumn {
  id: string;
  label: string;
  kind: 'amount' | 'total' | 'metric';
  value: (step: PackageStep) => number | null;
}

export const PACKAGE_DISPLAY_NAMES: Record<string, string> = {
  AdjustedParentContribution: 'Adjusted Parent Contribution',
  AdjustedStudentContribution: 'Adjusted Student Contribution',
  PellGrant: 'Pell Grant',
  InstitutionalGrantA: 'Institutional Grant A',
  InstitutionalGrantB: 'Institutional Grant B',
  InstitutionalGrantC: 'Institutional Grant C',
  InstitutionalScholarshipA: 'Institutional Scholarship A',
  InstitutionalScholarshipB: 'Institutional Scholarship B',
  InstitutionalScholarshipC: 'Institutional Scholarship C',
  StateGrantA: 'State Grant A',
  StateGrantB: 'State Grant B',
  StateGrantC: 'State Grant C',
  StudentLoanA: 'Student Loan A',
  StudentLoanB: 'Student Loan B',
  StudentLoanC: 'Student Loan C',
  StudentWork: 'Student Work',
  ParentLoan: 'Parent Loan',
  ConsolidatedSelfHelp: 'Consolidated Self Help',
  SchoolDefinedFund1: 'School Defined Fund 1',
  SchoolDefinedFund2: 'School Defined Fund 2',
};

export const CONTRIBUTION_PACK_IDS = new Set([
  'AdjustedParentContribution',
  'AdjustedStudentContribution',
]);

export const PACKAGE_SIDES: ReadonlyArray<{ key: PackageSide; label: string }> = [
  { key: 'published', label: 'Published' },
  { key: 'draft', label: 'Draft' },
];

export type MetricKey = MethodologyNeedKey | OutcomeKey;

export function methodologyNeedKey(institutionalMethod: boolean | null | undefined): MethodologyNeedKey | null {
  if (institutionalMethod === true) {
    return 'imNeed';
  }
  if (institutionalMethod === false) {
    return 'fmNeed';
  }
  return null;
}

export function metricDelta(step: PackageStep | null, key: MetricKey): number | null {
  if (!step?.before || step[key] === null || step.before[key] === null) {
    return null;
  }
  return step[key]! - step.before[key]!;
}

export function metricChanged(step: PackageStep | null, key: MetricKey): boolean {
  if (!step?.before) {
    return false;
  }
  return step.before[key] !== step[key];
}

export function formatDelta(delta: number | null): string {
  if (delta === null || delta === 0) {
    return '';
  }
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toLocaleString()}`;
}

export function ruleAmountLabel(rule: PackageRule): string {
  if (rule.assignType === 'Factor' && rule.factor !== null) {
    return `${rule.factor}%`;
  }
  if (rule.amount !== null) {
    return rule.amount.toLocaleString();
  }
  return '—';
}

export function snapshotValue(snapshot: TotalsSnapshot, columnId: string): number | null {
  switch (columnId) {
    case 'adjNeed':
      return snapshot.adjNeed;
    case 'remainingCoa':
      return snapshot.remainingCoa;
    case 'fmNeed':
      return snapshot.fmNeed;
    case 'imNeed':
      return snapshot.imNeed;
    default:
      return null;
  }
}

export function buildPackagingCompare(
  publishedRows: PackagePreviewRow[],
  draftRows: PackagePreviewRow[],
  previousExpanded: Map<string, boolean>,
): PackageCompare[] {
  const published = attachRunningContext(groupPreviewRows(publishedRows));
  const draft = attachRunningContext(groupPreviewRows(draftRows));
  const packIds = orderedPackIds(published, draft);

  return packIds.map(packId => {
    const pub = published.get(packId) ?? null;
    const drf = draft.get(packId) ?? null;
    const hasDifference = packagingOutcomesDiffer(pub, drf);

    return {
      packId,
      displayName: PACKAGE_DISPLAY_NAMES[packId] ?? packId,
      isContribution: CONTRIBUTION_PACK_IDS.has(packId),
      published: pub,
      draft: drf,
      hasDifference,
      expanded: previousExpanded.get(packId) ?? hasDifference,
    };
  });
}

export function ledgerColumns(
  sectionId: LedgerSectionId,
  showMethodologyNeed: boolean,
  methodologyKey: MethodologyNeedKey | null,
): LedgerColumn[] {
  const amountLabel = sectionId === 'contribution' ? 'Contribution' : 'Award';
  const columns: LedgerColumn[] = [
    { id: 'amount', label: amountLabel, kind: 'amount', value: step => step.amount },
  ];

  if (sectionId === 'aid' && showMethodologyNeed && methodologyKey) {
    columns.push({
      id: methodologyKey,
      label: methodologyKey === 'fmNeed' ? 'FM need' : 'IM need',
      kind: 'metric',
      value: step => step[methodologyKey],
    });
  }

  columns.push({
    id: 'adjNeed',
    label: 'Adj need',
    kind: 'total',
    value: step => step.adjNeed,
  });

  if (sectionId === 'aid') {
    columns.push({
      id: 'remainingCoa',
      label: 'Rem. COA',
      kind: 'metric',
      value: step => step.remainingCoa,
    });
  }

  return columns;
}

function packagingOutcomesDiffer(published: PackageStep | null, draft: PackageStep | null): boolean {
  if (!published && !draft) {
    return false;
  }
  if (!published || !draft) {
    return true;
  }
  if (published.amount !== draft.amount) {
    return true;
  }
  // Compare step impact, not running totals — a COA shift at the start
  // would otherwise mark every subsequent package as different.
  return stepImpact(published, 'adjNeed') !== stepImpact(draft, 'adjNeed')
    || stepImpact(published, 'remainingCoa') !== stepImpact(draft, 'remainingCoa');
}

function stepImpact(step: PackageStep, key: 'adjNeed' | 'remainingCoa'): number | null {
  if (!step.before || step[key] === null || step.before[key] === null) {
    return null;
  }
  return step[key]! - step.before[key]!;
}

function attachRunningContext(groups: Map<string, PackageStep>): Map<string, PackageStep> {
  let previous: TotalsSnapshot | null = null;

  for (const [packId, step] of groups) {
    step.before = previous ?? reconstructBefore(packId, step);
    previous = snapshotFromStep(step);
  }

  return groups;
}

function reconstructBefore(packId: string, step: PackageStep): TotalsSnapshot {
  const amount = step.amount ?? 0;
  const after = snapshotFromStep(step);

  if (CONTRIBUTION_PACK_IDS.has(packId)) {
    return {
      fmNeed: after.fmNeed,
      imNeed: after.imNeed,
      remainingCoa: after.remainingCoa,
      adjNeed: after.adjNeed === null ? null : after.adjNeed + amount,
    };
  }

  return {
    fmNeed: after.fmNeed === null ? null : after.fmNeed + amount,
    imNeed: after.imNeed === null ? null : after.imNeed + amount,
    adjNeed: after.adjNeed === null ? null : after.adjNeed + amount,
    remainingCoa: after.remainingCoa === null ? null : after.remainingCoa + amount,
  };
}

function snapshotFromStep(step: PackageStep): TotalsSnapshot {
  return {
    fmNeed: step.fmNeed,
    imNeed: step.imNeed,
    adjNeed: step.adjNeed,
    remainingCoa: step.remainingCoa,
  };
}

function orderedPackIds(
  published: Map<string, PackageStep>,
  draft: Map<string, PackageStep>,
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();

  for (const id of published.keys()) {
    ids.push(id);
    seen.add(id);
  }
  for (const id of draft.keys()) {
    if (!seen.has(id)) {
      ids.push(id);
      seen.add(id);
    }
  }
  return ids;
}

function groupPreviewRows(rows: PackagePreviewRow[]): Map<string, PackageStep> {
  const groups = new Map<string, PackageStep>();
  let current: PackageStep | null = null;
  let currentId = '';

  for (const row of rows) {
    const packId = row.packId || row.name;
    if (!packId) {
      continue;
    }

    if (isTotalRow(row)) {
      if (!current || currentId !== packId) {
        current = emptyStep();
        currentId = packId;
        groups.set(packId, current);
      }
      current.amount = nullableNumber(row.value);
      current.fmNeed = nullableNumber(row.fmNeed);
      current.imNeed = nullableNumber(row.imNeed);
      current.adjNeed = nullableNumber(row.adjNeed);
      current.remainingCoa = nullableNumber(row.adjCoa);
      current = null;
      currentId = '';
      continue;
    }

    if (!current || currentId !== packId) {
      current = emptyStep();
      currentId = packId;
      groups.set(packId, current);
    }
    current.rules.push({
      population: row.rulePopName || 'All',
      category: row.ruleType || '',
      assignType: row.ruleAssignType ?? '',
      factor: nullableNumber(row.ruleFactor),
      input: row.ruleInput ?? '',
      amount: nullableNumber(row.ruleAmount),
    });
  }

  return groups;
}

function emptyStep(): PackageStep {
  return {
    amount: null,
    fmNeed: null,
    imNeed: null,
    adjNeed: null,
    remainingCoa: null,
    rules: [],
    before: null,
  };
}

function isTotalRow(row: PackagePreviewRow): boolean {
  return row.adjCoa !== null && row.adjCoa !== undefined;
}

function nullableNumber(value: number | null | undefined): number | null {
  return value === null || value === undefined ? null : value;
}
